import {
  Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Grid, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Pagination,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { swalErrorBtn, swalErrorTimer, swalLoading, swalSuccessBtn } from '../../../helpers/swalHelper';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import Checkbox from '@mui/material/Checkbox';
import TablePagination from '@mui/material/TablePagination';
import dayjs from 'dayjs';
import LinearLoading from '../../LinearLoading';
import { isEmpty } from 'lodash';
import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import 'dayjs/locale/th';
import InvoiceListApi from '../../../service/invoice.api';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm, Controller } from 'react-hook-form';

export default function UploadInvoiceDialog({ open, onClose }) {
  const [soNumberList, setSONumberList] = useState([]);
  const [soNumberInput, setSONumberInput] = useState('');
  const [invoiceList, setInvoiceList] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isLinearLoading, setIsLinearLoading] = useState(false);

  const [selectedRows, setSelectedRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const indexNew = rowsPerPage * (page);
  const isSelected = (index) => selectedRows.includes(index);
  // console.log("selectedRows", selectedRows);

  const schema = (selectedRows) => {
    const requiredIfSelected = (message) =>
      yup.mixed().test('required-if-selected', message, function (value) {
        const path = this.path; // "rows[2].invoiceNumber"
        const index = parseInt(path.match(/rows\[(\d+)\]/)?.[1] ?? -1, 10);
        return !selectedRows.includes(index) || !!value;
      })

    const requiredIfSelectedDate = (message) =>
      yup.string().nullable().test('required-if-selected-date', message, function (value) {
        const path = this.path; // rows[1].invoiceStartDate
        const index = parseInt(path.match(/rows\[(\d+)\]/)?.[1] ?? -1, 10);
        return !selectedRows.includes(index) || !!value;
      });

    const requiredIfSelectedEither = (field1, field2, message) =>
      yup.string().nullable().test(
        `required-if-selected-either-${field1}-${field2}`,
        message,
        function (value) {
          const otherFieldValue = this.parent[field1 === 'taxId' ? 'customerCode' : 'taxId'];
          const path = this.path;
          const index = parseInt(path.match(/rows\[(\d+)\]/)?.[1] ?? -1, 10);
          return !selectedRows.includes(index) || !!value || !!otherFieldValue;
        }
      );

    return yup.object().shape({
      rows: yup.array().of(
        yup.object().shape({
          email: yup.string().nullable().test(
            'is-valid-email',
            'กรุณากรอกอีเมลในรูปแบบที่ถูกต้อง (กรอกหลายอีเมลได้โดยใช้จุลภาคคคั่น)',
            (value) => {
              if (value) {
                const emails = value.split(',').map((email) => email.trim());
                return emails.every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
              }
              return true;
            },
          ),
          invoiceNumber: requiredIfSelected('กรุณากรอกเลขที่ใบแจ้งหนี้'),
          taxId: requiredIfSelectedEither('taxId', 'customerCode', 'กรุณากรอก Tax ID หรือ Customer Code'),
          customerCode: requiredIfSelectedEither('customerCode', 'taxId', 'กรุณากรอก Customer Code หรือ Tax ID'),
          soName: requiredIfSelected('กรุณากรอกชื่อผู้ใช้บริการ'),
          address: requiredIfSelected('กรุณากรอกที่อยู่'),
          zipCode: requiredIfSelected('กรุณากรอกรหัสไปรษณีย์'),
          branchName: requiredIfSelected('กรุณากรอกสาขา'),
          soNumber: requiredIfSelected('กรุณากรอกเลข SO'),
          totalAmount: requiredIfSelected('กรุณากรอกจำนวนเงินทั้งหมด'),
          amountVat: requiredIfSelected('กรุณากรอกจำนวนเงินภาษีมูลค่าเพิ่ม'),
          grandTotal: requiredIfSelected('กรุณากรอกยอดรวมทั้งหมด'),
          invoiceStartDate: requiredIfSelectedDate('กรุณาเลือกวันที่สร้างใบแจ้งหนี้'),
          invoiceDueDate: requiredIfSelectedDate('กรุณาเลือกวันครบกำหนด'),
          periodStartDate: requiredIfSelectedDate('กรุณาเลือกวันที่เริ่มต้นระยะเวลา'),
          periodEndDate: requiredIfSelectedDate('กรุณาเลือกวันที่สิ้นสุดระยะเวลา'),
          descriptionList: yup.array().of(
            yup.object().shape({
              description: requiredIfSelected('กรุณากรอกรายละเอียด'),
              quantity: requiredIfSelected('กรุณากรอกจำนวน'),
              unitPrice: requiredIfSelected('กรุณากรอกราคาต่อหน่วย'),
              amount: requiredIfSelected('กรุณากรอกจำนวนเงิน'),
            })
          ),
        })
      ),
    });
  };

  const { register, handleSubmit, formState: { errors }, setValue, control } = useForm({
    resolver: yupResolver(schema(selectedRows)),
    defaultValues: { rows: [] }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rows',
  });

  const getInvoiceList = async () => {
    setInvoiceList([]);
    setPage(0);
    try {
      setSelectedRows([]);
      setIsLinearLoading(true);
      const data = {
        sonumber: soNumberList,
        startdate: startDate === null ? '' : dayjs(startDate).format('YYYY-MM-DD'),
        enddate: endDate === null ? '' : dayjs(endDate).format('YYYY-MM-DD'),
      };

      const resp = await InvoiceListApi.postInvoiceJVApi(data);
      // console.log("resp", resp);

      setInvoiceList(resp?.dataList || []);
      setTotal(resp?.dataList?.length);
    } catch (error) {
      console.log("error fetch data", error);
      setInvoiceList([]);
    } finally {
      setIsLinearLoading(false);
    }
  };

  const handleSONumberChange = (e) => {
    setSONumberInput(e.target.value);
    setSONumberList(
      e.target.value
        .split(',')
        .map(item => item.trim())
        .filter(item => item)
    );
  };

  const textFieldStyles = {
    '& fieldset': {
      borderRadius: 30,
    },
  };

  const handleSelectAllRows = (event) => {
    if (event.target.checked) {
      setSelectedRows(
        invoiceList
          .map((row, i) => (!row.isExist ? i : null)) // ถ้าไม่ isExist ก็เก็บ index
          .filter(i => i !== null) // ลบค่าที่เป็น null ออก
      );
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (index) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter((i) => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  const handleClearInvoice = () => {
    setSONumberInput('');
    setSONumberList([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedRows([]);
    setInvoiceList([]); 
    setPage(0); 
    setTotal(0);
    setRowsPerPage(5);
  }

  const onSubmit = async (item) => {
    const updatedData = item.rows.filter((_, index) => selectedRows.includes(index));

    if (isEmpty(updatedData)) {
      swalErrorBtn({ title: `กรุณาเลือกข้อมูลก่อนกดยืนยัน` });
      return;
    }

    try {
      swalLoading({title: "กำลังอัปโหลดข้อมูล"});
      // console.log("updatedData", updatedData);
      const data = await InvoiceListApi.uploadInvoiceApi(updatedData);
      if (data.status_message === 'create success') {
        swalSuccessBtn({
          title: 'อัปโหลดข้อมูลสำเร็จ'
        }).then((result) => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.log(error);
      swalErrorBtn({title: 'อัปโหลดข้อมูลไม่สำเร็จ'});
    }
  };

  const onError = (errors) => {
    swalErrorBtn({ title: `กรุณากรอกข้อมูลให้ครบถ้วน` });
  };

  const inputProps = {
    style: {
      textAlign: 'center',
      fontSize: '12px'
    }
  }

  const headers = [
    { label: 'เลข so', minWidth: 120 },
    { label: 'เลขที่ใบแจ้งหนี้', minWidth: 120 },
    { label: 'เลขประจำตัวผู้เสียภาษี', minWidth: 120 },
    { label: 'ชื่อลูกค้า', minWidth: 120 },
    { label: 'ที่อยู่ลูกค้า', minWidth: 120 },
    { label: 'รหัสไปรษณีย์', minWidth: 120 },
    { label: 'รหัสลูกค้า', minWidth: 120 },
    { label: 'สาขาของลูกค้า', minWidth: 120 },
    { label: 'วันที่สร้างใบแจ้งหนี้', minWidth: 120 },
    { label: 'วันครบกำหนด', minWidth: 120 },
    { label: 'รายการสินค้า', minWidth: 120 },
    { label: 'จำนวน', minWidth: 120, align: 'right' },
    { label: 'ราคาต่อหน่วย', minWidth: 120, align: 'right' },
    { label: 'จำนวนเงิน', minWidth: 120, align: 'right' },
    { label: 'Remark', minWidth: 150 },
    { label: 'วันที่เริ่มต้นระยะเวลา', minWidth: 120 },
    { label: 'วันที่สิ้นสุดระยะเวลา', minWidth: 120 },
    { label: 'จำนวนเงินก่อนภาษีมูลค่าเพิ่ม', minWidth: 120, align: 'right' },
    { label: 'จำนวนเงินภาษีมูลค่าเพิ่ม', minWidth: 120, align: 'right' },
    { label: 'ยอดรวมทั้งหมด (ยอดชำระ)', minWidth: 120, align: 'right' },
    { label: 'อีเมล', minWidth: 120 }
  ];

  const handleKeyEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      getInvoiceList();
    }
  };

  const renderTextFieldCell = (fieldName, rowSpan, row, globalIndex) => (
    <TableCell align="center" rowSpan={rowSpan}>
      <TextField
        variant="standard"
        disabled={row.isExist}
        {...register(`rows.${globalIndex}.${fieldName}`)}
        defaultValue={row[fieldName]}
        inputProps={inputProps}
        error={!!errors?.rows?.[globalIndex]?.[fieldName]}
        helperText={errors?.rows?.[globalIndex]?.[fieldName]?.message}
      />
    </TableCell>
  );

  const renderNumberInputCell = (fieldName, rowSpan, row, globalIndex) => (
    <TableCell align="center" rowSpan={rowSpan}>
      <TextField
        variant="standard"
        disabled={row.isExist}
        {...register(`rows.${globalIndex}.${fieldName}`)}
        inputProps={{
          type: 'number',
          step: '0.01',
          min: 0,
          ...inputProps
        }}
        defaultValue={row[fieldName]}
        error={!!errors?.rows?.[globalIndex]?.[fieldName]}
        helperText={errors?.rows?.[globalIndex]?.[fieldName]?.message}
      />
    </TableCell>
  );

  const renderDescNumberInputCell = (fieldName, row, globalIndex, descIndex ) => (
    <TableCell>
      <TextField
        disabled={row.isExist}
        defaultValue={row[fieldName]}
        inputProps={{
          type: 'number',
          step: fieldName === 'quantity' ? '1' : '0.01',
          min: 0,
          ...inputProps,
        }}
        {...register(`rows.${globalIndex}.descriptionList.${descIndex}.${fieldName}`)}
        variant="standard"
        error={!!errors?.rows?.[globalIndex]?.descriptionList?.[descIndex]?.[fieldName]}
        helperText={errors?.rows?.[globalIndex]?.descriptionList?.[descIndex]?.[fieldName]?.message}
      />
    </TableCell>
  );

  const renderDatePickerCell = (fieldName, rowSpan, row, globalIndex) => (
    <TableCell align="center" rowSpan={rowSpan}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          name={`rows.${globalIndex}.${fieldName}`}
          control={control}
          render={({ field }) => (
            <DatePicker
              value={field.value ? dayjs(field.value) : null}
              onChange={(newValue) => {
                field.onChange(
                  newValue ? dayjs(newValue).format('YYYY-MM-DD') : null
                );
              }}
              inputFormat="DD/MM/YYYY"
              disabled={row.isExist}
              inputProps={inputProps}
              renderInput={(params) => (
                  <TextField
                  {...params}
                  variant="standard"
                  error={!!errors?.rows?.[globalIndex]?.[fieldName]}
                  helperText={errors?.rows?.[globalIndex]?.[fieldName]?.message}
                  />
              )}
            />
          )}
        />
      </LocalizationProvider>
    </TableCell>
  );

  useEffect(() => {
    if (invoiceList) {
      remove();
      invoiceList.forEach((row) => {
        append({ ...row });
      });
    }
  }, [invoiceList, append, remove, setValue])

  return (
    <Dialog 
        open={open} 
        onClose={() => {onClose(); handleClearInvoice();}} 
        maxWidth='xl' 
        PaperProps={{ style: { minWidth: 'auto', maxWidth: 'auto' } }}
        fullWidth
    >
        <DialogTitle>
          นำเข้าข้อมูล <ImportExportIcon color='primary' />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4} lg={4} sx={{ mt: 2 }}>
                <Paper
                  sx={{
                    pl: 2,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '20px',
                    justifyContent: 'space-between',
                    boxShadow: 'none',
                    border: '1px solid #CCCCCC',
                    py: '3px'
                  }}
                >
                  <InputBase
                    value={soNumberInput}
                    onChange={handleSONumberChange}
                    fullWidth
                    placeholder="ค้นหาเลข SO"
                    onKeyDown={handleKeyEnter}
                  />
                </Paper>
              </Grid>
              <Grid item xs={6} md={3} lg={2} sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="วันที่"
                    value={startDate}
                    inputFormat="DD/MM/YYYY"
                    maxDate={dayjs()}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        sx={{ ...textFieldStyles }}
                        size="small"
                        {...params}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6} md={3} lg={2} sx={{ mt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disabled={ startDate === null || startDate === '' }
                      label="ถึง"
                      inputFormat="DD/MM/YYYY"
                      value={endDate}
                      minDate={startDate}
                      onChange={(newValue) => {
                        setEndDate(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          sx={{ ...textFieldStyles }}
                          {...params}
                        />
                      )}
                    />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={12} lg={2} sx={{ mt: 2 }}>
                <Button onClick={() => getInvoiceList()} variant='contained' sx={{ borderRadius: 30 ,color: '#ffffff' }} >ค้นหา</Button>
                <Button onClick={() => handleClearInvoice()} variant='outlined' sx={{ borderRadius: 30, ml: 2}} >Clear</Button>
              </Grid>
            </Grid>
          </DialogContentText>
              <TableContainer component={Paper} sx={{ mt: 3, maxHeight: 400 }}>
              <LinearLoading isLinearLoading={isLinearLoading}/>
                <Table size="small" stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ size: '12px' }}>
                        <Checkbox
                          onChange={(e) => handleSelectAllRows(e)}
                        />
                      </TableCell>
                      {headers.map((header, index) => (
                        <TableCell key={index} align={header.align || 'center'} sx={{ fontSize: '12px', minWidth: header.minWidth }}>
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  {_.size(fields) ? (
                  <TableBody>
                    {fields.slice(page * rowsPerPage, Math.min(fields.length, (page + 1) * rowsPerPage),).map((row, index) => {
                      const globalIndex = page * rowsPerPage + index;
                      const descriptions = row.descriptionList || [];
                      const descriptionCount = descriptions.length || 1;

                      return descriptions.map((descItem, descIndex) => (
                        <TableRow
                          key={`${globalIndex}-${descIndex}`}
                          sx={{ backgroundColor: row.isExist ? '#eeeeee97' : '' }}
                        >
                          {descIndex === 0 && (
                            <>
                              <TableCell rowSpan={descriptionCount} align="center">
                                <Checkbox
                                  disabled={row.isExist}
                                  checked={isSelected(globalIndex)}
                                  onChange={() => handleSelectRow(globalIndex)}
                                />
                              </TableCell>
                              {renderTextFieldCell('soNumber', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('invoiceNumber', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('taxId', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('soName', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('address', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('zipCode', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('customerCode', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('branchName', descriptionCount, row, globalIndex)}
                              {renderDatePickerCell('invoiceStartDate', descriptionCount, row, globalIndex)}
                              {renderDatePickerCell('invoiceDueDate', descriptionCount, row, globalIndex)}
                            </>
                          )}

                          {/* แสดง description แยกรายการ */}
                          <TableCell>
                            <TextField
                              variant="standard"
                              disabled={row.isExist}
                              {...register(`rows.${globalIndex}.descriptionList.${descIndex}.description`)}
                              defaultValue={row.description}
                              inputProps={inputProps}
                              error={!!errors?.rows?.[globalIndex]?.descriptionList?.[descIndex]?.description}
                              helperText={
                              errors?.rows?.[globalIndex]?.descriptionList?.[descIndex]?.description?.message
                              }
                            />
                          </TableCell>

                          {renderDescNumberInputCell('quantity', row, globalIndex, descIndex)}
                          {renderDescNumberInputCell('unitPrice', row, globalIndex, descIndex)}
                          {renderDescNumberInputCell('amount', row, globalIndex, descIndex)}

                          {descIndex === 0 && (
                            <>
                              <TableCell rowSpan={descriptionCount} sx={{ color: row.isExist ? '#7c7c7cff' : '' }}> 
                                <TextareaAutosize
                                  disabled={row.isExist}
                                  defaultValue={row.remark}
                                  {...register(`rows.${globalIndex}.remark`)}
                                  style={{
                                    textAlign: 'left',
                                    width: '160px',
                                    resize: 'none',
                                    border: 'none',
                                    borderBottom: '1px solid #AFAFAF',
                                    paddingBottom: '4px',
                                    outline: 'none',
                                    background: 'none',
                                    fontSize: '12px'
                                  }}
                                  variant="standard"
                                  inputProps={inputProps}
                                />
                              </TableCell>
                              {renderDatePickerCell('periodStartDate', descriptionCount, row, globalIndex)}
                              {renderDatePickerCell('periodEndDate', descriptionCount, row, globalIndex)}
                              {renderNumberInputCell('totalAmount', descriptionCount, row, globalIndex)}
                              {renderNumberInputCell('amountVat', descriptionCount, row, globalIndex)}
                              {renderNumberInputCell('grandTotal', descriptionCount, row, globalIndex)}
                              {renderTextFieldCell('email', descriptionCount, row, globalIndex)}
                            </>
                          )}
                        </TableRow>
                      ));
                    })}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={20} align="center">
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                rowsPerPage={rowsPerPage}
                page={page}
                count={total}
                onPageChange={(event, newPage) => {setPage(newPage);}}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit(onSubmit, onError)}> ยืนยัน </Button>
          <Button onClick={() => { handleClearInvoice(); onClose(); }} color='error'>ปิด</Button>
        </DialogActions>
    </Dialog>
  );
}
